// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Typography, TableRow, TableCell } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import InfoIcon from '@mui/icons-material/Info';
import downloadIcon from 'Assets/download-icon.png';
import moment from 'moment-timezone';
import { GetProjectDetailsRoute } from 'Utils/routes';
import { Link as RouterLink } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PDF from 'Features/order/PDF';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { WideLightTooltip } from 'Components/Tooltip';
import OrderSummary from './orderSummary';
import stylesDef from './styles';
import { GetPdfDataThunk } from 'Features/order/orderThunk';
import { CircularProgress } from '@mui/material';

moment.tz.setDefault('America/New_York');

interface ComponentProps {
  order: IOrder;
}

export const OrderListItem: React.FC<ComponentProps> = ({ order }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.projectList.list?.find((item: IProject) => item._id === order.ownerProject));

  const { orgTeams } = useAppSelector((state) => state.account);
  const team = orgTeams?.find((t) => t._id === order.team);

  const [pdfData, setPdfData] = useState<any>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (order) {
      setPdfLoading(true);
      const fetchPdfData = async () => {
        const result = await dispatch(GetPdfDataThunk(order._id));
        if (result.success) {
          const pdfData = {
            ...result.data.pdfData,
            type: 'Invoice',
          }
          setPdfData(pdfData);
        }
        setPdfLoading(false);
      };

      fetchPdfData();
    }

    return () => {
      setPdfData(null);
    };
  }, []);

  return (
    <TableRow key={order._id}>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {order.name}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {order.referenceId || "N/A"}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {team?.name || "N/A"}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {/* If orderDate is undefined display N/A */}
        {order.orderDate
          ? moment(order.orderDate).format("MMMM D, YYYY")
          : "N/A"}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {project.type === 'utilities' ? "N/A" : order.acreage.toFixed(2)}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {project.type !== 'utilities' ? "N/A" : order.kmlLength?.toFixed(2)}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        <Box sx={styles.info}>
          {(order.expressAICreditAcreage ? order.expressAICreditAcreage : order.creditAcreage).toFixed(2)}
          <WideLightTooltip
            placement="bottom"
            title={<OrderSummary order={order} />}
          >
            <InfoIcon sx={styles.icon} />
          </WideLightTooltip>
        </Box>
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        <Box sx={styles.info}>
          {order.costToCustomer ? `$${order.costToCustomer.toFixed(2)}` : "N/A"}
          <WideLightTooltip
            placement="bottom"
            interactive
            title={
              <Box>
                <Box sx={styles.divGrid}>
                  <Typography variant="body1" sx={styles.text}>
                    Project cost
                  </Typography>
                  <Typography sx={styles.text} variant="body1">
                    {order.costToCustomer >= 0
                      ? `$${order.costToCustomer.toFixed(2)}`
                      : "N/A"}
                  </Typography>
                </Box>
                {order?.salesTaxAmount > 0 && (
                  <Box sx={styles.divGrid}>
                    <Typography variant="body1" sx={styles.text}>
                      Sales tax
                    </Typography>
                    <Typography sx={styles.text} variant="body1">
                      {`$${order.salesTaxAmount.toFixed(2)}`}
                    </Typography>
                  </Box>
                )}
                <Box sx={styles.divGrid}>
                  <Typography variant="body1" sx={styles.text}>
                    Additional cost
                  </Typography>
                  <Typography sx={styles.text} variant="body1">
                    {order.paidForThisProject >= 0
                      ? `$${order.paidForThisProject.toFixed(2)}`
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>
            }
          >
            <InfoIcon sx={styles.icon} />
          </WideLightTooltip>
        </Box>
      </TableCell>
      <TableCell sx={styles.iconTableCell} component="th" scope="row">
        {pdfLoading && <CircularProgress size={24} sx={styles.buttonProgress} />}
        {!pdfLoading && pdfData && (
          <PDFDownloadLink document={<PDF data={pdfData} />} fileName={`${pdfData?.projectName}_invoice.pdf`}>
            {({ loading }) =>
              (loading ? <CircularProgress size={24} sx={styles.buttonProgress} /> : (
                <Box
                  sx={styles.downloadText}
                  onClick={() => {
                    window?.pendo?.track('Invoice downloaded', { projectId: order?.ownerProject });
                  }}
                >
                  <img src={downloadIcon} alt="download" />
                </Box>
              ))}
          </PDFDownloadLink>
        )}
      </TableCell>
      {!_ADMIN_ && (
        <TableCell sx={styles.tableCell} component="th" scope="row">
          <RouterLink
            to={GetProjectDetailsRoute(order.ownerProject)}
            css={styles.routerLink}
            title="View Project"
          >
            <Typography variant="h6">View Project</Typography>
          </RouterLink>
        </TableCell>
      )}
    </TableRow>
  );
};

export default OrderListItem;
